import normalize from "@/util/normalize.mjs";

export const SCORE_MIN = 0 as const;
export const SCORE_MAX = 1 as const;

/**
 * The calculated performance for a given stat. Should be populated by the `statPerformance` function.
 * @see {@link statPerformance}
 */
export interface StatPerformance {
  /** The lower bound for benchmarking this stat */
  min: number;
  /** The upper bound for benchmarking this stat */
  max: number;
  /** The mean value for this stat */
  mean: number;
  /** The raw value for this stat */
  val: number;
  /**
   * The calculated score for this stat
   * @see {@link calculateScore}
   */
  score: number;
  /** If true, a lower value for this stat is better */
  lowerIsBetter: boolean;
}

/**
 * Calculates a score between {@link SCORE_MIN} and {@link SCORE_MAX} for the value between the specified bounds.
 *
 * @param val The value to score
 * @param min The lower bound of the scoring range
 * @param max The upper bound of the scoring range
 * @returns A score between {@link SCORE_MIN} and {@link SCORE_MAX} corresponding to where val falls between min and max.
 */
export function calculateScore(val: number, min: number, max: number): number {
  if (!min && !max) return 0;
  const score = normalize(val, max, min);

  return score > SCORE_MAX ? SCORE_MAX : score < SCORE_MIN ? SCORE_MIN : score;
}

/**
 * Calculates the bound for a value deviating from a specified mean.
 *
 * @param bound Override value for the bound. The function will return this instead of the calculated value if provided
 * @param mean The mean value to calculate as the midpoint of the bound
 * @param diff The deviation from the mean for calculating the bound.
 * @param isLowerBetter If a lower value is better. Inverts the results for the lower/upper bound.
 * @param isCalculatingMin True if calculating the lower bound, false if calculating the upper bound.
 * @returns The calculated bound from the data passed in.
 */
export function calculateBound(
  bound: number | null = null,
  mean: number,
  diff: number,
  isLowerBetter: boolean,
  isCalculatingMin: boolean,
): number {
  if (bound) {
    return bound; // Use the explicitly provided bound if available
  }
  // Calculate bound based on whether lower is better and which bound we're calculating
  if (isLowerBetter) {
    return isCalculatingMin ? mean + diff : mean - diff;
  }
  return isCalculatingMin ? mean - diff : mean + diff;
}

/**
 * Parameters provided to calculate the performance for a given stat.
 *
 * @see {@link statPerformance}
 */
export interface StatPerformanceParams {
  /** The value to compare to the benchmark */
  playerVal: number;
  /** The mean value to compare the playerVal to. */
  benchmarkMean: number;
  /** The standard deviation from the mean value */
  benchmarkStdev: number;
  /** A multiplier to apply to the standard deviation when calculating the lower bound for this stat. @default 1.5 */
  lowerBoundStdevMultiplier?: number;
  /** A multiplier to apply to the standard deviation when calculating the upper bound for this stat. @default 1.5 */
  upperBoundStdevMultiplier?: number;
  /** If true, a lower value for this stat will be considered a better value @default false */
  lowerIsBetter?: boolean;
  /** If false, the lower bound will be clamped to zero @default false */
  canBeNegative?: boolean;
  /** An overriding lower bound for the statistic */
  min?: number;
  /** An overriding upper bound for the statistic */
  max?: number;
  /** If true, the mean and bounds will be rounded to the nearest integer @default false */
  round?: boolean;
}

/**
 * Calculate's a player's performance relative to a given mean and standard deviation. By default, this applies a 1.5x multiplier
 * to the standard deviation to calculate the lower and upper bounds for scoring.
 *
 * @param params {@link StatPerformanceParams}
 * @returns An object summarizing a player's performance, including the calculated or provided bounds, a score between 0 and 1, and the provider playerVal
 */
export function statPerformance(
  params: StatPerformanceParams,
): StatPerformance {
  const {
    playerVal = 0,
    benchmarkMean = 0,
    benchmarkStdev = 0,
    lowerBoundStdevMultiplier = 1.5,
    upperBoundStdevMultiplier = 1.5,
    lowerIsBetter = false,
    canBeNegative = false,
    min: lowerBound,
    max: upperBound,
    round = false,
  } = params;
  const lowerDiff = benchmarkStdev * lowerBoundStdevMultiplier;
  const upperDiff = benchmarkStdev * upperBoundStdevMultiplier;

  let min = calculateBound(
    lowerBound,
    benchmarkMean,
    lowerDiff,
    lowerIsBetter,
    true,
  );
  const max = calculateBound(
    upperBound,
    benchmarkMean,
    upperDiff,
    lowerIsBetter,
    false,
  );

  if (min < 0 && !canBeNegative) min = 0;

  return {
    min: round ? Math.round(min) : min,
    max: round ? Math.round(max) : max,
    mean: round ? Math.round(benchmarkMean) : benchmarkMean,
    val: round ? Math.round(playerVal) : playerVal,
    score: calculateScore(round ? Math.round(playerVal) : playerVal, min, max),
    lowerIsBetter,
  };
}
